import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import {
  AccountTreeOutlined,
  Add,
  ArrowUpward,
  BlockOutlined,
  CheckCircleOutline,
  ChevronLeft,
  ChevronRight,
  Close,
  ContentCopyOutlined,
  DescriptionOutlined,
  DragIndicator,
  EditOutlined,
  ErrorOutline,
  EventRepeatOutlined,
  GroupsOutlined,
  HistoryOutlined,
  LockOutlined,
  NotesOutlined,
  RestartAltOutlined,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { Loader } from "components/Loader";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalConfirmDialog, globalTextFieldSx } from "components/DesignSystem/sharedUiPatterns";
import { tooltipStyleProps } from "services/constants/Styles";
import { designColors, designRadii, designShell, designType, designWeights } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField, toOptions } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { PayInputAuditTrail } from "pages/PayRoll/PayInputs/Shared/AuditTrail";
import {
  CountPill,
  EmptyRow,
  MethodCards,
  Required,
  SimpleTable,
  ToneChip,
  VersionChip,
  downloadCsv,
  multilineFieldSx,
  searchFieldSx,
  tableCellSx,
  tableHeadSx,
} from "../Shared/salaryUi";
import { formatDay } from "../SalaryComponents/salaryComponentData";
import {
  CATEGORY_NOUNS,
  OPTIONAL_CATEGORIES,
  PAY_BASIS_OPTIONS,
  PERIOD,
  TEMPLATE_CATEGORIES,
  TEMPLATE_METHODS,
  allowedCodes,
  categoryCounts,
  compareLines,
  componentUpdates,
  currentTemplateVersion,
  describeCalculation,
  describeLine,
  evaluateTemplate,
  formatRupees,
  isOverridden,
  latestVersionNumber,
  lineCategory,
  lineDefault,
  lineErrors,
  lineFromComponent,
  lineName,
  medianGross,
  orderChanged,
  templateEmployeeCount,
  templateStatus,
  upgradedLine,
  validateTemplateForm,
  versionLabel,
  withBalancing,
  canBalance,
  normalizeBalancing,
} from "./salaryTemplateData";

const EMPLOYEES_PER_PAGE = 8;
const SALARY_REVISION_PATH = "/admin/payroll/salary/salary-revision";
const PAYROLL_HOME_PATH = "/admin/payroll/setup";

// Status chip colours (shared status chip, as on the Pay Inputs lists), in
// the colours of the matching list cards.
export const TEMPLATE_STATUS_COLORS = { Used: "#F0954A", Unused: "#12B76A", Inactive: "#6B7280" };

const money = (value) => (value == null ? "–" : formatRupees(value));
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// The browser's calculation, with the server's preview on top when there is
// one (amounts, totals and the errors that would block a save).
// Server messages come as "field: text" ("structure: Earnings add up to
// …"): the field name is dropped. The "add up to" message is left out because
// the screen's own balance line already says it. A "balancing component"
// message is kept: balancing is the user's choice now, so if the server
// insists on one the user has to see it.
const cleanServerErrors = (errors = []) => [
  ...new Set(
    errors
      .map((message) => String(message).replace(/^(structure|non_field_errors|components|detail|error)\s*:\s*/i, "").trim())
      .filter((message) => message && !/add up to/i.test(message)),
  ),
];

const TOTAL_KEYS = ["earnings", "otherEarnings", "deductions", "otherDeductions", "employer", "reimbursement"];

export const mergePreview = (local, server, gross) => {
  if (!server || (server.gross != null && Math.abs(server.gross - gross) >= 0.01)) return { ...local, errors: [] };
  const totals = Object.fromEntries(TOTAL_KEYS.map((key) => [key, server[key] ?? local[key] ?? 0]));
  const round = (value) => Math.round(value * 100) / 100;
  return {
    ...local,
    ...totals,
    values: { ...local.values, ...server.values },
    net: server.net ?? round(totals.earnings + totals.otherEarnings - totals.deductions - totals.otherDeductions),
    ctc: server.ctc ?? round(totals.earnings + totals.otherEarnings + totals.employer),
    balanced: Math.abs(totals.earnings - gross) < 0.01,
    errors: cleanServerErrors(server.errors),
  };
};

// Where each section's rows go in GROSS / NET / CTC, shown under its title.
export const CATEGORY_INFO = {
  "Additional Earnings": "Paid on top of GROSS (Other Earnings). They add to NET and CTC.",
  Deductions: "Taken off GROSS.",
  "Additional Deductions": "Taken off after the deductions (Other Deduction).",
  "Employer Contribution": "Paid by the employer on top of the salary. Counts towards CTC only.",
  Reimbursement: "Paid outside the salary: not part of GROSS, NET or CTC.",
  Calculation: "Helper values that formulas can use. Not paid and not in any total.",
};

// Categories the screen shows: Earnings and Deductions always, the others
// when the company has a component in them or the lines have a row.
// The totals under the sections, in payslip order: [label, result key,
// strong]. Additional Earnings / Deductions, Employer Contribution, CTC and
// Reimbursement show only when the template has such rows.
export const totalRows = (catalog, ...lineSets) => {
  const has = (category) => lineSets.some((lines) => lines.some((line) => lineCategory(catalog, line) === category));
  return [
    ["GROSS", "earnings", false],
    ...(has("Additional Earnings") ? [["Additional Earnings", "otherEarnings", false]] : []),
    ["Deductions", "deductions", false],
    ...(has("Additional Deductions") ? [["Additional Deductions", "otherDeductions", false]] : []),
    ["NET before statutory", "net", true],
    ...(has("Employer Contribution") ? [["Employer Contribution", "employer", false]] : []),
    ...(has("Employer Contribution") || has("Additional Earnings") ? [["CTC before statutory", "ctc", true]] : []),
    ...(has("Reimbursement") ? [["Reimbursement (outside salary)", "reimbursement", false]] : []),
  ];
};

export const visibleCategories = (catalog, lines) =>
  TEMPLATE_CATEGORIES.filter(
    (category) =>
      !OPTIONAL_CATEGORIES.includes(category) ||
      catalog.some((item) => item.category === category && item.active) ||
      lines.some((line) => lineCategory(catalog, line) === category),
  );

// ---------------- Shared parts (also used by Assign Salary / Revision) ----------------

export const GrossInput = ({ value, onChange, label = "Gross" }) => (
  <Mui.TextField
    size="small"
    type="number"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    InputProps={{ startAdornment: <Mui.InputAdornment position="start">₹</Mui.InputAdornment> }}
    inputProps={{ "aria-label": label, min: 0, style: { textAlign: "right" } }}
    sx={{ ...globalTextFieldSx, width: 170, flexShrink: 0 }}
  />
);

export const CheckLine = ({ ok, children }) => (
  <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: ok ? "#067647" : "#DC2626" }}>
    {ok ? <CheckCircleOutline sx={{ fontSize: 18 }} /> : <ErrorOutline sx={{ fontSize: 18 }} />}
    <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold }}>{children}</Mui.Typography>
  </Mui.Stack>
);

export const ComponentCell = ({ catalog, line, tags, meta }) => (
  <Mui.Box sx={{ minWidth: 0 }}>
    <Mui.Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
      <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.medium, color: designColors.slate900 }}>
        {lineName(catalog, line)}
      </Mui.Typography>
      {line.balancing ? <ToneChip label="Balancing" tone="green" /> : null}
      {tags}
    </Mui.Stack>
    {meta ? (
      <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.25 }}>
        <Mui.Typography component="span" sx={designType.captionText}>{line.code}</Mui.Typography>
        {meta}
      </Mui.Stack>
    ) : (
      <Mui.Typography component="div" sx={{ ...designType.captionText, mt: 0.25 }}>
        {line.code}
      </Mui.Typography>
    )}
  </Mui.Box>
);

export const overrideLabel = (catalog, line) => {
  const base = lineDefault(catalog, line);
  if (!base) return "";
  if (base.method !== line.method) return `default ${describeCalculation(base)}`;
  if (line.method === "Fixed Amount") return `default ${formatRupees(base.amount)}`;
  if (line.method === "Percentage") return `default ${base.percentage}%${base.base !== line.base ? ` of ${base.base}` : ""}`;
  return `default ${base.formula}`;
};

export const inputSx = { ...globalTextFieldSx, width: "100%", "& .MuiOutlinedInput-root": { ...globalTextFieldSx["& .MuiOutlinedInput-root"], fontSize: 14 } };

export const ValueInput = ({ catalog, lines, line, onChange, error }) => {
  if (line.balancing) {
    return (
      <Mui.Box>
        <Mui.TextField size="small" value={line.formula} disabled sx={inputSx} />
        <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>Auto · balances earnings to GROSS</Mui.Typography>
      </Mui.Box>
    );
  }
  if (line.method === "Fixed Amount") {
    return (
      <Mui.TextField
        size="small"
        type="number"
        value={line.amount}
        error={Boolean(error)}
        onChange={(event) => onChange({ amount: event.target.value })}
        InputProps={{ startAdornment: <Mui.InputAdornment position="start">₹</Mui.InputAdornment> }}
        inputProps={{ "aria-label": `${line.code} amount`, min: 0 }}
        sx={inputSx}
      />
    );
  }
  if (line.method === "Percentage") {
    // One base per row for now (multi-select comes back once the backend
    // reads several codes). A base the list doesn't have, e.g. a
    // component's own "BASIC + DA", stays as an option so it still shows.
    const bases = allowedCodes(catalog, lines, line.code);
    if (line.base && !bases.includes(line.base)) bases.unshift(line.base);
    return (
      <Mui.Stack direction="row" spacing={1}>
        <Mui.TextField
          size="small"
          type="number"
          value={line.percentage}
          error={Boolean(error)}
          onChange={(event) => onChange({ percentage: event.target.value })}
          InputProps={{ endAdornment: <Mui.InputAdornment position="end">%</Mui.InputAdornment> }}
          inputProps={{ "aria-label": `${line.code} percentage`, min: 0, max: 100 }}
          sx={{ ...inputSx, width: "45%" }}
        />
        <Mui.TextField
          size="small"
          select
          value={line.base}
          onChange={(event) => onChange({ base: event.target.value })}
          inputProps={{ "aria-label": `${line.code} base` }}
          sx={{ ...inputSx, width: "55%" }}
        >
          {bases.map((code) => (
            <Mui.MenuItem key={code} value={code}>of {code}</Mui.MenuItem>
          ))}
        </Mui.TextField>
      </Mui.Stack>
    );
  }
  return (
    <Mui.TextField
      size="small"
      value={line.formula}
      error={Boolean(error)}
      onChange={(event) => onChange({ formula: event.target.value })}
      placeholder="e.g. MIN(BASIC * 10%, 2000)"
      inputProps={{ "aria-label": `${line.code} formula`, spellCheck: false }}
      sx={inputSx}
    />
  );
};

export const AddComponentPicker = ({ catalog, category, lines, payBasis, onAdd, hideStatutory = false }) => {
  const [anchor, setAnchor] = useState(null);
  const [search, setSearch] = useState("");
  const options = catalog.filter(
    (item) =>
      item.category === category &&
      item.active &&
      !item.system &&
      !(hideStatutory && item.statutory) &&
      !lines.some((line) => line.code === item.code) &&
      `${item.name} ${item.code}`.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const [label, labels] = CATEGORY_NOUNS[category] || [category.toLowerCase(), category.toLowerCase()];
  return (
    <>
      <Mui.Button
        startIcon={<Add />}
        onClick={(event) => { setSearch(""); setAnchor(event.currentTarget); }}
        sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark, border: `1px dashed ${designColors.slate300}`, borderRadius: designRadii.card, px: 2 }}
      >
        Add {label}
      </Mui.Button>
      <Mui.Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { ...designShell.actionMenuPaper, width: 420, maxWidth: "calc(100vw - 32px)", mt: 0.5, p: 0 } }}
      >
        <Mui.Box sx={{ p: 1.5 }}>
          <Mui.TextField size="small" autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${labels}`} sx={{ ...searchFieldSx, width: "100%" }} />
        </Mui.Box>
        <Mui.Box sx={{ maxHeight: 280, overflowY: "auto" }}>
          {options.length ? (
            options.map((item) => (
              <Mui.ButtonBase
                key={item.code}
                onClick={() => { onAdd(item.code); setAnchor(null); }}
                sx={{ width: "100%", display: "flex", justifyContent: "space-between", gap: 2, px: 2, py: 1.25, borderTop: `1px solid ${designColors.slate100}`, textAlign: "left", "&:hover": { backgroundColor: designColors.slateBg } }}
              >
                <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.medium, color: designColors.slate900 }}>
                  {item.name} <Mui.Box component="span" sx={designType.captionText}>{item.code}</Mui.Box>
                </Mui.Typography>
                <Mui.Typography sx={{ ...designType.captionText, whiteSpace: "nowrap" }}>{describeLine(item.versions[item.versions.length - 1])}</Mui.Typography>
              </Mui.ButtonBase>
            ))
          ) : (
            <Mui.Typography sx={{ ...designType.helperText, px: 2, py: 1.5 }}>No more active {labels} to add.</Mui.Typography>
          )}
        </Mui.Box>
        <Mui.Typography sx={{ ...designType.captionText, px: 2, py: 1.25, backgroundColor: designColors.slateBg, borderTop: `1px solid ${designColors.slate200}` }}>
          Active {category} only. Fixed amounts are <b>per {PERIOD[payBasis]}</b> in this template.
        </Mui.Typography>
      </Mui.Popover>
    </>
  );
};

// ---------------- View: header card ----------------

// Same look as the employee card on the Pay Inputs views: icon, name +
// status, then an inline Pay basis | Components | Version strip.
const TemplateHeaderCard = ({ template }) => {
  const version = currentTemplateVersion(template);
  const status = templateStatus(template);
  const components = template.components_count ?? version.lines.length;
  const item = (value, withDivider) => (
    <Mui.Typography
      component="span"
      sx={{
        pl: withDivider ? 2 : 0,
        ml: withDivider ? 0.25 : 0,
        borderLeft: withDivider ? `1px solid ${designColors.slate200}` : "none",
        color: designColors.slate800,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Mui.Typography>
  );
  return (
    <Mui.Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${designColors.slate200}`,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${designColors.tealTint} 0%, ${designColors.white} 100%)`,
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
      }}
    >
      <Mui.Stack direction="row" spacing={2} alignItems="center">
        <Mui.Avatar sx={{ width: 56, height: 56, backgroundColor: template.active ? designColors.tealDark : designColors.slate400, color: designColors.white }}>
          <DescriptionOutlined />
        </Mui.Avatar>
        <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
          <Mui.Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
            <Mui.Typography
              title={template.name}
              sx={{ fontSize: { xs: 17, sm: 18 }, fontWeight: 700, color: designColors.slate900, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {template.name}
            </Mui.Typography>
            <RequestStatusChip status={status} colorCode={TEMPLATE_STATUS_COLORS[status]} />
          </Mui.Stack>
          <Mui.Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.2, rowGap: 1 }}>
            {item(template.pay_basis, false)}
            {item(plural(components || 0, "component"), true)}
            {item(versionLabel(version.version), true)}
          </Mui.Stack>
        </Mui.Box>
      </Mui.Stack>
    </Mui.Box>
  );
};

// ---------------- View: Configuration ----------------

const ConfigurationTab = ({ catalog, template, canEdit, onUpgrade, gross, setGross, serverPreview, revisePrompt }) => {
  const history = useHistory();
  const version = currentTemplateVersion(template);
  const lines = withBalancing(catalog, version.lines);
  const grossValue = Number(gross) > 0 ? Number(gross) : medianGross(template);
  const result = mergePreview(evaluateTemplate(catalog, version.lines, grossValue), serverPreview, grossValue);
  const updates = componentUpdates(catalog, version.lines);
  const employees = templateEmployeeCount(template);
  const lastChanged = `${formatDay(version.created_at)} · ${versionLabel(version.version)}`;
  const { indigo, pink, blue, green, orange } = detailAccentPalette;

  const section = (category) => {
    const rows = lines.filter((line) => lineCategory(catalog, line) === category);
    if (!rows.length) return null;
    return (
      <>
        <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
          <Mui.TableCell colSpan={3} sx={{ ...tableHeadSx, fontSize: 14 }}>{category} · {rows.length}</Mui.TableCell>
          <Mui.TableCell align="right" sx={{ ...tableHeadSx, fontSize: 14 }}>Amount</Mui.TableCell>
        </Mui.TableRow>
        {rows.map((line) => {
          const update = updates.find((item) => item.line.code === line.code);
          const latest = update ? catalog.find((item) => item.code === line.code)?.versions.find((item) => item.version === update.latest) : null;
          const cellSx = { ...tableCellSx, borderBottom: update ? "none" : tableCellSx.borderBottom };
          return (
            <React.Fragment key={line.code}>
              <Mui.TableRow>
                <Mui.TableCell sx={cellSx}><ComponentCell catalog={catalog} line={line} /></Mui.TableCell>
                <Mui.TableCell sx={{ ...cellSx, width: 70 }}><VersionChip label={versionLabel(line.component_version)} /></Mui.TableCell>
                <Mui.TableCell sx={cellSx}>
                  <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <span>{describeLine(line)}</span>
                    {isOverridden(catalog, line) ? <ToneChip label={`Overridden · ${overrideLabel(catalog, line)}`} tone="purple" /> : null}
                  </Mui.Stack>
                </Mui.TableCell>
                <Mui.TableCell align="right" sx={{ ...cellSx, fontWeight: designWeights.semibold }}>{money(result.values[line.code])}</Mui.TableCell>
              </Mui.TableRow>
              {update ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={4} sx={{ ...tableCellSx, pt: 0 }}>
                    <Mui.Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                      sx={{ backgroundColor: "#EAF2FF", borderRadius: designRadii.card, px: 2, py: 1.25 }}
                    >
                      <Mui.Typography sx={{ fontSize: 14, color: "#1D4ED8" }}>
                        <ArrowUpward sx={{ fontSize: 16, verticalAlign: "-3px", mr: 0.5 }} />
                        <b>{line.code} {versionLabel(update.latest)} available</b>
                        {latest ? ` · ${describeCalculation(latest)}` : ""} (this template uses {versionLabel(line.component_version)})
                      </Mui.Typography>
                      {canEdit ? (
                        <PrimaryButton onClick={() => onUpgrade(template, line.code)} sx={{ minWidth: 0, minHeight: 36, px: 2 }}>Upgrade</PrimaryButton>
                      ) : null}
                    </Mui.Stack>
                  </Mui.TableCell>
                </Mui.TableRow>
              ) : null}
            </React.Fragment>
          );
        })}
      </>
    );
  };
  const total = (label, value, strong) => (
    <Mui.TableRow sx={{ backgroundColor: strong ? designColors.slateBg : undefined }}>
      <Mui.TableCell colSpan={3} sx={{ ...tableCellSx, fontSize: strong ? 16 : 14, fontWeight: strong ? designWeights.heading : 400 }}>{label}</Mui.TableCell>
      <Mui.TableCell align="right" sx={{ ...tableCellSx, fontSize: strong ? 16 : 14, fontWeight: strong ? designWeights.heading : 400 }}>{money(value)}</Mui.TableCell>
    </Mui.TableRow>
  );

  return (
    <Mui.Stack spacing={2}>
      {!template.active && employees ? (
        <RequestInfoBanner tone="warning" title={`Still used by ${employees} employees.`} body="It can't be assigned or used for revisions while inactive." />
      ) : null}
      {revisePrompt ? (
        <Mui.Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ backgroundColor: "#FFF7ED", border: "1px solid #F2D680", borderRadius: designRadii.card, px: 2, py: 1.5 }}
        >
          <Mui.Typography sx={{ fontSize: 14, color: "#A15C00" }}>{revisePrompt}</Mui.Typography>
          <PrimaryButton onClick={() => history.push(SALARY_REVISION_PATH)} sx={{ minWidth: 0, flexShrink: 0 }}>Revise Now</PrimaryButton>
        </Mui.Stack>
      ) : null}
      {updates.length && template.active ? (
        <RequestInfoBanner
          title={`${plural(updates.length, "component update")} available.`}
          body={`Upgrading saves this template as ${versionLabel(version.version + (employees ? 1 : 0))}.${employees ? ` The ${employees} employees stay on ${versionLabel(version.version)} until revised.` : ""}`}
        />
      ) : null}

      <RequestDetailsSection
        title="TEMPLATE DETAILS"
        fields={[
          { icon: EventRepeatOutlined, label: "Pay Basis", value: template.pay_basis, helper: `Fixed amounts are per ${PERIOD[template.pay_basis] || "period"}`, accent: indigo },
          ...(template.description ? [{ icon: NotesOutlined, label: "Description", value: template.description, accent: pink, multiline: true }] : []),
          {
            icon: AccountTreeOutlined,
            label: "Components",
            value: categoryCounts(catalog, lines).join(" · ") || "None",
            accent: blue,
          },
          { icon: GroupsOutlined, label: "Employees", value: String(employees), accent: green },
          { icon: HistoryOutlined, label: "Last Changed", value: lastChanged, accent: orange },
        ]}
      />

      <Mui.Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, backgroundColor: designColors.white, px: 2, py: 1.5 }}
      >
        <Mui.Box>
          <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>Preview at GROSS</Mui.Typography>
          <Mui.Typography sx={designType.captionText}>
            {template.employees.length ? `Median of ${template.employees.length} employees` : `Default for ${template.pay_basis}`}
          </Mui.Typography>
        </Mui.Box>
        {/* View screen: the GROSS is shown, not edited. */}
        <Mui.Typography
          aria-label="Preview GROSS"
          sx={{ fontSize: 16, fontWeight: designWeights.semibold, color: designColors.slate900, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {money(grossValue)}
        </Mui.Typography>
      </Mui.Stack>
      {lines.length ? (
        <>
          <Mui.Box sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, overflowX: "auto", backgroundColor: designColors.white }}>
            <Mui.Table size="small" sx={{ minWidth: 520, "& tr:last-child td": { borderBottom: "none" } }}>
              <Mui.TableBody>
                {TEMPLATE_CATEGORIES.map((category) => <React.Fragment key={category}>{section(category)}</React.Fragment>)}
                {totalRows(catalog, lines).map(([label, key, strong]) => (
                  <React.Fragment key={label}>{total(label, result[key], strong)}</React.Fragment>
                ))}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.Box>
          <CheckLine ok={result.balanced}>
            {result.balanced
              ? `Earnings add up to GROSS (${money(grossValue)})`
              : `Earnings add up to ${money(result.earnings)}, not GROSS (${money(grossValue)}). Edit and save the template to fix this.`}
          </CheckLine>
          {result.errors.map((message) => <CheckLine key={message} ok={false}>{message}</CheckLine>)}
        </>
      ) : (
        <RequestInfoBanner body="This template has no components yet." />
      )}
      <RequestInfoBanner body="Statutory PF, ESI and PT are added at payroll, so they don't appear in templates." />
    </Mui.Stack>
  );
};

// ---------------- View: Employees ----------------

const useEmployeeFilters = (template) => {
  const [search, setSearch] = useState("");
  const [version, setVersion] = useState("");
  const [structure, setStructure] = useState("");
  const [page, setPage] = useState(0);
  useEffect(() => {
    setSearch(""); setVersion(""); setStructure(""); setPage(0);
  }, [template.id]);
  useEffect(() => setPage(0), [search, version, structure]);
  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return template.employees.filter(
      (row) =>
        (!query || row.name.toLowerCase().includes(query) || row.employee_code.toLowerCase().includes(query)) &&
        (!version || versionLabel(row.version) === version) &&
        (!structure || (structure === "Customized") === row.customized),
    );
  }, [template.employees, search, version, structure]);
  return { search, setSearch, version, setVersion, structure, setStructure, page, setPage, rows };
};

const EmployeesTab = ({ template, filters }) => {
  const history = useHistory();
  const { page, setPage, rows } = filters;
  const current = currentTemplateVersion(template).version;
  const older = template.employees.filter((row) => row.version < current);
  const olderVersions = [...new Set(older.map((row) => versionLabel(row.version)))];
  const customized = template.employees.filter((row) => row.customized).length;
  const versionNumbers = [...new Set(template.employees.map((row) => row.version))].sort((a, b) => b - a);
  const counts = versionNumbers.map((number) => ({ label: versionLabel(number), version: number, count: template.employees.filter((row) => row.version === number).length }));
  const total = rows.length;
  const from = total ? page * EMPLOYEES_PER_PAGE + 1 : 0;
  const to = Math.min(total, (page + 1) * EMPLOYEES_PER_PAGE);
  const lastPage = Math.max(0, Math.ceil(total / EMPLOYEES_PER_PAGE) - 1);
  const pageRows = rows.slice(page * EMPLOYEES_PER_PAGE, (page + 1) * EMPLOYEES_PER_PAGE);
  return (
    <Mui.Stack spacing={2}>
      {older.length && template.active ? (
        <Mui.Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ backgroundColor: "#FFF7ED", border: "1px solid #F2D680", borderRadius: designRadii.card, px: 2, py: 1.5 }}
        >
          <Mui.Typography sx={{ fontSize: 14, color: "#A15C00" }}>
            <b>{older.length} employee{older.length === 1 ? " is" : "s are"} on {olderVersions.join(", ")}.</b> Move them to {versionLabel(current)} with a Template-Based Revision. Customized employees are left unselected.
          </Mui.Typography>
          <PrimaryButton onClick={() => history.push(SALARY_REVISION_PATH)} sx={{ minWidth: 0, flexShrink: 0 }}>Revise to {versionLabel(current)}</PrimaryButton>
        </Mui.Stack>
      ) : null}
      <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)" }, gap: 1.5 }}>
        <Mui.TextField size="small" value={filters.search} onChange={(event) => filters.setSearch(event.target.value)} placeholder="Search name or code" sx={searchFieldSx} />
        <SelectField value={filters.version} onChange={filters.setVersion} options={toOptions(counts.map((item) => item.label))} placeholder="All versions" />
        <SelectField value={filters.structure} onChange={filters.setStructure} options={toOptions(["Template Linked", "Customized"])} placeholder="All structures" />
      </Mui.Box>
      {counts.length || customized ? (
        <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {counts.map((item) => (
            <CountPill key={item.label} label={`${item.count} on ${item.label}`} tone={item.version === current ? "green" : "amber"} />
          ))}
          {customized ? <CountPill label={`${customized} customized`} tone="purple" /> : null}
        </Mui.Stack>
      ) : null}
      {/* Full values, no truncation: scrolls sideways when wider than the panel. */}
      <SimpleTable
        fit
        heads={[
          { label: "Employee" },
          { label: "Template Version", width: 120 },
          { label: "Structure", width: 120 },
          { label: "Gross", align: "right", width: 100 },
          { label: "Effective From", width: 104 },
        ]}
      >
        {pageRows.length ? (
          pageRows.map((row) => (
            <Mui.TableRow key={row.id}>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.medium, color: designColors.slate900 }}>{row.name || "–"}</Mui.Typography>
                <Mui.Typography sx={designType.captionText}>{[row.employee_code, row.department].filter(Boolean).join(" · ")}</Mui.Typography>
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Stack direction="row" spacing={0.75}>
                  <VersionChip label={versionLabel(row.version)} current={row.version === current} />
                  {row.version < current ? <ToneChip label="Older" tone="amber" /> : null}
                </Mui.Stack>
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <ToneChip label={row.customized ? "Customized" : "Template Linked"} tone={row.customized ? "purple" : "slate"} />
              </Mui.TableCell>
              <Mui.TableCell align="right" sx={tableCellSx}>
                <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{money(row.gross)}</Mui.Typography>
                <Mui.Typography sx={designType.captionText}>/ {PERIOD[template.pay_basis]}</Mui.Typography>
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{formatDay(row.effective_from)}</Mui.TableCell>
            </Mui.TableRow>
          ))
        ) : (
          <EmptyRow colSpan={5} text={template.employees.length ? "No employees match these filters." : "No employee has this template yet."} />
        )}
      </SimpleTable>
      {total ? (
        <Mui.Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
          <Mui.Typography sx={designType.captionText}>Showing {from}–{to} of {total}</Mui.Typography>
          <Mui.Stack direction="row" spacing={1}>
            <SecondaryButton aria-label="Previous page" sx={{ minWidth: 42, px: 0 }} disabled={page === 0} onClick={() => setPage((value) => value - 1)}>
              <ChevronLeft />
            </SecondaryButton>
            <SecondaryButton aria-label="Next page" sx={{ minWidth: 42, px: 0 }} disabled={page >= lastPage} onClick={() => setPage((value) => value + 1)}>
              <ChevronRight />
            </SecondaryButton>
            <SecondaryButton
              onClick={() =>
                downloadCsv(
                  `${template.name.replace(/[^A-Za-z0-9]+/g, "_")}_employees.csv`,
                  ["Employee", "Code", "Department", "Template version", "Structure", "Gross", "Per", "Salary effective from"],
                  rows.map((row) => [row.name, row.employee_code, row.department, versionLabel(row.version), row.customized ? "Customized" : "Template Linked", row.gross ?? "", PERIOD[template.pay_basis], formatDay(row.effective_from)]),
                )
              }
            >
              Export
            </SecondaryButton>
          </Mui.Stack>
        </Mui.Stack>
      ) : null}
    </Mui.Stack>
  );
};

// ---------------- View: Activity Log ----------------

// Shared Pay Inputs activity format, built from the template's version
// history (template-versions; no activity endpoint yet): one entry per
// version with its components and employees, what changed from the version
// before when both carry their rows, and the change note. Newest first.
const isCreatedNote = (note) => /^\s*(template\s+)?created\.?\s*$/i.test(String(note || ""));

const templateActivity = (catalog, template) => {
  const versions = [...template.versions].sort((a, b) => a.version - b.version);
  const latest = versions[versions.length - 1];
  const entries = versions.map((version, index) => {
    const previous = versions[index - 1];
    const employees = version.employee_count ?? template.employees.filter((row) => row.version === version.version).length;
    const components = version.lines.length || version.components_count;
    const changes =
      previous && previous.lines.length && version.lines.length
        ? compareLines(catalog, previous.lines, version.lines)
            .filter((row) => row.change !== "Unchanged")
            .map((row) => ({
              label: `${row.name} (${row.change === "Upgraded" ? `${versionLabel(row.fromVersion)} → ${versionLabel(row.toVersion)}` : row.change})`,
              from: row.before,
              to: row.after,
            }))
        : [];
    return {
      id: `version-${version.version}`,
      type: previous ? "updated" : "created",
      title: previous ? `Saved as ${versionLabel(version.version)}` : `Created as ${versionLabel(version.version)}`,
      tags: [version === latest ? { label: "Current", tone: "green" } : { label: "Superseded", tone: "slate" }],
      actor: { name: version.created_by || "System", role: "" },
      at: version.created_at,
      changes,
      details: [
        ...(components ? [{ label: "Components", value: String(components) }] : []),
        { label: "Employees", value: employees ? String(employees) : "None" },
      ],
      note: isCreatedNote(version.note) ? "" : version.note || "",
    };
  });
  if (!template.active && template.deactivation) {
    entries.push({
      id: "deactivated",
      type: "status",
      title: "Deactivated",
      actor: { name: template.deactivation.by || "System", role: "" },
      at: template.deactivation.at,
      changes: [{ label: "Status", from: "Active", to: "Inactive" }],
      note: template.deactivation.reason ? `Reason: ${template.deactivation.reason}` : "",
    });
  }
  return entries.reverse();
};

const ActivityTab = ({ catalog, template }) => (
  <Mui.Stack spacing={2}>
    <PayInputAuditTrail entries={templateActivity(catalog, template)} />
    <RequestInfoBanner body="A template version changes only templates. Employees move to a newer version through a Template-Based Revision." />
  </Mui.Stack>
);

// ---------------- View panel ----------------

export const SalaryTemplateViewPanel = ({
  open,
  template,
  catalog,
  canEdit,
  busy,
  detailsLoading,
  serverPreview,
  gross,
  onGrossChange,
  revisePrompt,
  expanded,
  onToggleExpand,
  onClose,
  onEdit,
  onUpgrade,
  onDuplicate,
  onDeactivate,
  onReactivate,
  onDelete,
}) => {
  const history = useHistory();
  const [tab, setTab] = useState("configuration");
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reason, setReason] = useState("");
  const id = template?.id;
  useEffect(() => {
    setTab("configuration");
    setDeactivateOpen(false);
    setReason("");
  }, [id]);
  const employeeFilters = useEmployeeFilters(template || { id: null, employees: [] });

  if (!template) return null;
  const status = templateStatus(template);
  const employees = templateEmployeeCount(template);
  const editable = canEdit && template.active;

  const confirmDeactivate = async () => {
    if (!reason.trim()) return;
    // Stays open with the reason when the server refuses.
    if (!(await onDeactivate(template, reason.trim()))) return;
    setDeactivateOpen(false);
    setReason("");
  };

  // Record actions, full width like the Pay Inputs views: Deactivate (and
  // Delete while Unused), or Reactivate.
  let footer = null;
  if (editable) {
    footer = (
      <Mui.Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
        <Mui.Button
          variant="outlined"
          onClick={() => setDeactivateOpen(true)}
          disabled={busy}
          startIcon={<BlockOutlined sx={{ fontSize: 17 }} />}
          sx={{
            ...designShell.panelActionButton,
            flex: 1,
            minWidth: 0,
            minHeight: 42,
            color: designColors.slate700,
            borderColor: designColors.slate300,
            backgroundColor: designColors.white,
            "&:hover": { borderColor: designColors.slate400, backgroundColor: "#F8FAFC" },
          }}
        >
          Deactivate Template
        </Mui.Button>
        {status === "Unused" && onDelete ? (
          <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
            <RequestCancelButton onClick={() => onDelete(template)} disabled={busy}>Delete Template</RequestCancelButton>
          </Mui.Box>
        ) : null}
      </Mui.Stack>
    );
  } else if (canEdit && !template.active) {
    footer = (
      <PrimaryButton onClick={() => onReactivate(template)} disabled={busy} startIcon={<RestartAltOutlined />} sx={{ width: "100%" }}>
        Reactivate Template
      </PrimaryButton>
    );
  }

  const iconButton = (title, icon, onClick) => (
    <Mui.Tooltip title={title} arrow slotProps={tooltipStyleProps}>
      <span>
        <Mui.IconButton
          size="small"
          aria-label={title}
          onClick={onClick}
          disabled={busy || detailsLoading}
          sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
        >
          {icon}
        </Mui.IconButton>
      </span>
    </Mui.Tooltip>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={onClose}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Salary Template Details"
        subtitle="View the template's components, employees and versions."
        breadcrumb={[
          { label: "Payroll", onClick: () => history.push(PAYROLL_HOME_PATH) },
          "Salary",
          { label: "Salary Templates", onClick: onClose },
          "Template Details",
        ]}
        bodySx={panelBodySx}
        headerActions={
          editable ? (
            <Mui.Stack direction="row" spacing={1}>
              {iconButton("Duplicate as…", <ContentCopyOutlined fontSize="small" />, () => onDuplicate(template))}
              {iconButton("Edit", <EditOutlined fontSize="small" />, () => onEdit(template))}
            </Mui.Stack>
          ) : null
        }
        footer={footer}
      >
        <Mui.Box sx={{ mb: 2 }}>
          <TemplateHeaderCard template={template} />
        </Mui.Box>
        <Mui.Tabs
          value={tab}
          onChange={(event, value) => setTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ ...designShell.panelTabs, mt: 0, mb: 2, borderBottom: `1px solid ${designColors.slate200}` }}
        >
          <Mui.Tab label="Configuration" value="configuration" />
          <Mui.Tab label={`Employees (${employees})`} value="employees" />
          <Mui.Tab label="Activity Log" value="activity" />
        </Mui.Tabs>

        {/* Every tab comes from the template's own APIs, loaded as it opens. */}
        {detailsLoading ? <Loader info="Loading..." minHeight="30vh" /> : null}
        {tab === "configuration" && !detailsLoading ? (
          <ConfigurationTab
            catalog={catalog}
            template={template}
            canEdit={editable}
            onUpgrade={onUpgrade}
            gross={gross}
            setGross={onGrossChange}
            serverPreview={serverPreview}
            revisePrompt={revisePrompt}
          />
        ) : null}
        {tab === "employees" && !detailsLoading ? <EmployeesTab template={template} filters={employeeFilters} /> : null}
        {tab === "activity" && !detailsLoading ? <ActivityTab catalog={catalog} template={template} /> : null}
      </GlobalSidePanel>

      <GlobalConfirmDialog
        open={deactivateOpen}
        title={`Deactivate ${template.name}`}
        onCancel={() => {
          if (busy) return;
          setDeactivateOpen(false);
          setReason("");
        }}
        onConfirm={confirmDeactivate}
        confirmLabel="Deactivate"
        cancelLabel="Keep Active"
        loading={busy}
        loadingLabel="Deactivating..."
        maxWidth="sm"
        confirmDisabled={!reason.trim()}
        reasonValue={reason}
        onReasonChange={setReason}
        reasonPlaceholder="Why is this template being deactivated?"
        danger
      >
        {/* The shared dialog puts the reason box right under the text. */}
        <Mui.Box sx={{ mb: 2 }}>
          It's hidden from Assign Salary and Template-Based Revision.{" "}
          {employees
            ? `The ${employees} employees on it keep their salary until they're revised.`
            : "No employee has it, so nobody is affected."}{" "}
          You can reactivate it any time.
        </Mui.Box>
      </GlobalConfirmDialog>
    </>
  );
};

// ---------------- Add / Edit ----------------

// One balancing earning: GROSS minus the other earnings. The earning that
// stops balancing gets its default calculation back.
const makeBalancing = (catalog, lines, code) =>
  lines.map((line) => {
    if (line.code === code) return { ...line, balancing: true };
    if (!line.balancing) return line;
    const base = lineDefault(catalog, line);
    return base
      ? { ...line, balancing: false, method: base.method, amount: base.amount, percentage: base.percentage, base: base.base || "GROSS", formula: base.formula }
      : { ...line, balancing: false };
  });

// The user turns balancing off again: the row gets its default calculation back.
const removeBalancing = (catalog, lines, code) =>
  lines.map((line) => {
    if (line.code !== code) return line;
    const base = lineDefault(catalog, line);
    return base
      ? { ...line, balancing: false, method: base.method, amount: base.amount, percentage: base.percentage, base: base.base || "GROSS", formula: base.formula }
      : { ...line, balancing: false };
  });

const LinesEditor = ({ catalog, category, form, setLines, original, result, gross, errors, showErrors }) => {
  const dragCode = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const lines = withBalancing(catalog, form.lines);
  const rows = lines.filter((line) => lineCategory(catalog, line) === category);
  const reviewCodes = (form.review_codes || []).map(String);
  const updateLine = (code, change) => setLines(form.lines.map((line) => (line.code === code ? { ...line, ...change } : line)));
  const setMethod = (code, method) => updateLine(code, { method, amount: "", percentage: "", base: "GROSS", formula: "" });
  const resetLine = (line) => {
    const base = lineDefault(catalog, line);
    if (base) updateLine(line.code, { method: base.method, amount: base.amount, percentage: base.percentage, base: base.base || "GROSS", formula: base.formula });
  };
  const upgradeLine = (line) => setLines(form.lines.map((item) => (item.code === line.code ? upgradedLine(catalog, item) : item)));
  const removeLine = (code) => setLines(form.lines.filter((line) => line.code !== code));
  const toggleBalancing = (line) =>
    setLines(line.balancing ? removeBalancing(catalog, form.lines, line.code) : makeBalancing(catalog, form.lines, line.code));
  const addLine = (code) => {
    // Added rows are never Balancing by themselves; the user sets it.
    const added = { ...lineFromComponent(catalog, code), balancing: false };
    // New earnings go above the balancing line so it stays last.
    const balancingIndex = form.lines.findIndex((line) => line.balancing);
    if (category === "Earnings" && balancingIndex >= 0 && !added.balancing) {
      setLines([...form.lines.slice(0, balancingIndex), added, ...form.lines.slice(balancingIndex)]);
    } else {
      setLines([...form.lines, added]);
    }
  };
  const drop = (targetCode) => {
    const from = dragCode.current;
    dragCode.current = null;
    setDragOver(null);
    if (!from || from === targetCode) return;
    const next = [...form.lines];
    const [moved] = next.splice(next.findIndex((line) => line.code === from), 1);
    next.splice(next.findIndex((line) => line.code === targetCode), 0, moved);
    setLines(next);
  };
  const originalLine = (code) => original?.lines.find((line) => line.code === code);
  // Every cell starts at the top so the inputs on a row line up even when one
  // cell has notes under its box; text cells drop to the inputs' centre line.
  const cellSx = { ...tableCellSx, verticalAlign: "top" };
  const textCellSx = { ...cellSx, pt: "20px" };
  const linkSx = { fontSize: 12.5, fontWeight: designWeights.semibold, color: designColors.tealDark };
  return (
    <FormSection
      title={`${category} · ${rows.length}`}
      info={
        category === "Earnings"
          ? "Drag rows to set the payslip order. Earnings must add up to GROSS: adjust the values, or use Set as Balancing on one earning so it takes GROSS minus the others."
          : `${CATEGORY_INFO[category] || ""} Drag rows to set the payslip order.`.trim()
      }
    >
      <Mui.Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
        <Mui.Box sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, overflowX: "auto" }}>
          <Mui.Table size="small" sx={{ minWidth: 760 }}>
            <Mui.TableHead>
              <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
                <Mui.TableCell sx={{ ...tableHeadSx, width: 28, px: 1 }} />
                <Mui.TableCell sx={tableHeadSx}>Component</Mui.TableCell>
                <Mui.TableCell sx={{ ...tableHeadSx, width: 160 }}>Method</Mui.TableCell>
                <Mui.TableCell sx={{ ...tableHeadSx, width: 260 }}>Value / Formula</Mui.TableCell>
                <Mui.TableCell align="right" sx={{ ...tableHeadSx, width: 110, whiteSpace: "nowrap" }}>At {formatRupees(gross)}</Mui.TableCell>
                <Mui.TableCell sx={{ ...tableHeadSx, width: 44 }} />
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {rows.map((line) => {
                const before = originalLine(line.code);
                const latest = line.latest_version ?? latestVersionNumber(catalog, line.code);
                const canUpgrade = (latest && latest > line.component_version) || (line.upgrade_available && !line.upgrade);
                // Shown as soon as the value is wrong, not only after Save.
                const error = errors[line.code];
                const overridden = isOverridden(catalog, line);
                const review = reviewCodes.includes(String(line.code)) || reviewCodes.includes(String(line.component_id));
                return (
                  <Mui.TableRow
                    key={line.code}
                    draggable
                    onDragStart={(event) => { dragCode.current = line.code; event.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(event) => { if (dragCode.current && rows.some((row) => row.code === dragCode.current)) { event.preventDefault(); setDragOver(line.code); } }}
                    onDragLeave={() => setDragOver((current) => (current === line.code ? null : current))}
                    onDrop={(event) => { event.preventDefault(); drop(line.code); }}
                    onDragEnd={() => { dragCode.current = null; setDragOver(null); }}
                    sx={{
                      backgroundColor: review ? "#FFF7ED" : original && !before ? "#ECFDF3" : line.upgrade ? "#F0F7FF" : undefined,
                      boxShadow: dragOver === line.code ? `inset 0 2px 0 ${designColors.teal}` : "none",
                    }}
                  >
                    <Mui.TableCell sx={{ ...textCellSx, px: 1, cursor: "grab", color: designColors.slate400 }}>
                      <DragIndicator fontSize="small" />
                    </Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      <ComponentCell
                        catalog={catalog}
                        line={line}
                        tags={
                          <>
                            {review ? <ToneChip label={`Check for ${form.pay_basis}`} tone="amber" /> : null}
                            {original && !before ? <ToneChip label="New" tone="green" /> : null}
                            {line.upgrade && before ? <ToneChip label={`Upgraded from ${versionLabel(before.component_version)}`} tone="blue" /> : null}
                          </>
                        }
                        meta={
                          <>
                            <VersionChip label={versionLabel(line.component_version)} current={!canUpgrade} />
                            {canUpgrade ? (
                              <Mui.Link component="button" type="button" underline="hover" onClick={() => upgradeLine(line)} sx={{ ...linkSx, color: "#1D4ED8" }}>
                                {latest > line.component_version ? `${versionLabel(latest)} available · ` : ""}Upgrade
                              </Mui.Link>
                            ) : null}
                            {category === "Earnings" && (line.balancing || canBalance(catalog, line)) ? (
                              <Mui.Link component="button" type="button" underline="hover" onClick={() => toggleBalancing(line)} sx={linkSx}>
                                {line.balancing ? "Remove Balancing" : "Set as Balancing"}
                              </Mui.Link>
                            ) : null}
                          </>
                        }
                      />
                    </Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      <Mui.TextField
                        size="small"
                        select
                        value={line.method}
                        disabled={line.balancing}
                        onChange={(event) => setMethod(line.code, event.target.value)}
                        inputProps={{ "aria-label": `${line.code} method` }}
                        sx={inputSx}
                      >
                        {TEMPLATE_METHODS.map((method) => (
                          <Mui.MenuItem key={method} value={method}>{method}</Mui.MenuItem>
                        ))}
                      </Mui.TextField>
                    </Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      <ValueInput catalog={catalog} lines={lines} line={line} error={error} onChange={(change) => updateLine(line.code, change)} />
                      {error ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.5 }}>{error}</Mui.Typography> : null}
                      {overridden ? (
                        <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <ToneChip label={`Overridden · ${overrideLabel(catalog, line)}`} tone="purple" />
                          {lineDefault(catalog, line) ? (
                            <Mui.Link component="button" type="button" underline="hover" onClick={() => resetLine(line)} sx={linkSx}>Reset</Mui.Link>
                          ) : null}
                        </Mui.Stack>
                      ) : null}
                    </Mui.TableCell>
                    <Mui.TableCell align="right" sx={{ ...textCellSx, fontWeight: designWeights.semibold, whiteSpace: "nowrap" }}>{money(result.values[line.code])}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, pt: "15px", px: 0.5 }}>
                      <Mui.IconButton size="small" aria-label={`Remove ${lineName(catalog, line)}`} onClick={() => removeLine(line.code)}>
                        <Close fontSize="small" />
                      </Mui.IconButton>
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
              {!rows.length ? <EmptyRow colSpan={6} text={`No ${(CATEGORY_NOUNS[category] || [])[1] || category.toLowerCase()} yet.`} /> : null}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.Box>
        <Mui.Box sx={{ pt: 1.5 }}>
          <AddComponentPicker catalog={catalog} category={category} lines={form.lines} payBasis={form.pay_basis} onAdd={addLine} hideStatutory />
        </Mui.Box>
      </Mui.Box>
    </FormSection>
  );
};

const PreviewCard = ({ catalog, form, original, gross, setGross, grossHint, result, beforeResult, newVersionLabel, beforeLabel }) => {
  const lines = withBalancing(catalog, form.lines);
  const compare = Boolean(beforeResult);
  const beforeLines = original ? withBalancing(catalog, original.lines) : [];
  const names = (category) => [
    ...new Set([...lines, ...(compare ? beforeLines : [])].filter((line) => lineCategory(catalog, line) === category).map((line) => line.code)),
  ];
  const lineByCode = (code) => lines.find((line) => line.code === code) || beforeLines.find((line) => line.code === code) || { code };
  const amountCell = (value, strong, muted) => (
    <Mui.Typography sx={{ fontSize: 14, width: compare ? 70 : "auto", textAlign: "right", color: muted ? designColors.slate500 : designColors.slate900, fontWeight: strong ? designWeights.semibold : 400 }}>
      {value == null ? "—" : compare ? Number(value).toLocaleString("en-IN") : money(value)}
    </Mui.Typography>
  );
  // pick reads one amount from a result (this version's or the one before).
  const row = (key, label, pick, strong) => (
    <Mui.Stack key={key} direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75 }}>
      <Mui.Typography sx={{ fontSize: 14, flex: 1, color: designColors.slate700, fontWeight: strong ? designWeights.heading : 400 }}>{label}</Mui.Typography>
      {compare ? amountCell(pick(beforeResult), false, true) : null}
      {amountCell(pick(result), strong || compare)}
    </Mui.Stack>
  );
  const heading = (label) => (
    <Mui.Stack direction="row" spacing={1.5} sx={{ mt: 1.5, mb: 0.25 }}>
      <Mui.Typography sx={{ ...designType.captionText, flex: 1, letterSpacing: "0.04em", fontWeight: designWeights.semibold }}>{label}</Mui.Typography>
      {compare ? (
        <>
          <Mui.Typography sx={{ ...designType.captionText, width: 70, textAlign: "right", fontWeight: designWeights.semibold }}>{beforeLabel}</Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, width: 70, textAlign: "right", fontWeight: designWeights.semibold }}>{newVersionLabel}</Mui.Typography>
        </>
      ) : null}
    </Mui.Stack>
  );
  const divider = <Mui.Box sx={{ borderTop: `1px solid ${designColors.slate200}`, my: 0.5 }} />;
  return (
    <FormSection
      title={compare ? `Preview · ${beforeLabel} vs ${newVersionLabel}` : "Preview"}
      info="Amounts at a sample gross, worked out by the server as you edit. Not saved."
    >
      <Mui.Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
        <Mui.Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>Gross per {PERIOD[form.pay_basis]}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>{grossHint}</Mui.Typography>
          </Mui.Box>
          <GrossInput value={gross} onChange={setGross} label={`Gross per ${PERIOD[form.pay_basis]}`} />
        </Mui.Stack>
        {TEMPLATE_CATEGORIES.map((category) =>
          category === "Earnings" || names(category).length ? (
            <React.Fragment key={category}>
              {heading(`${CATEGORY_NOUNS[category][1].toUpperCase()}${category === "Calculation" ? " · NOT PAID" : ""}`)}
              {names(category).map((code) => row(code, lineName(catalog, lineByCode(code)), (value) => value.values[code]))}
            </React.Fragment>
          ) : null,
        )}
        {divider}
        {totalRows(catalog, lines, compare ? beforeLines : []).map(([label, key, strong]) => row(`total-${key}`, label, (value) => value[key], strong))}
      </Mui.Box>
    </FormSection>
  );
};

// Same layout as the Pay Inputs forms: title + clickable breadcrumb, a
// banner for the save rule, white section cards with a one-line info note,
// an info line under every field label, and the shared footer. Opens
// expanded (the builder needs the width); the preview sits beside it.
export const SalaryTemplateFormPanel = ({
  open,
  mode,
  initial,
  template,
  templates,
  catalog,
  payBases,
  defaultGross,
  busy,
  expanded,
  onToggleExpand,
  onClose,
  onSave,
  onPreview,
}) => {
  const history = useHistory();
  const [form, setForm] = useState(initial);
  const [gross, setGross] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [serverPreview, setServerPreview] = useState(null);
  const grossFor = (basis) => medianGross({ pay_basis: basis, employees: [] }, defaultGross);
  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...initial, lines: normalizeBalancing(catalog, initial.lines || []) } : initial);
    setShowErrors(false);
    setServerErrors({});
    setServerPreview(null);
    setGross(String(template ? medianGross(template, defaultGross) : grossFor(initial?.pay_basis)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, template]);

  const editing = mode === "edit" ? template : null;
  const original = editing ? currentTemplateVersion(editing) : null;
  const used = Boolean(editing && templateStatus(editing) === "Used");
  const changes = useMemo(
    () => (original && form ? compareLines(catalog, original.lines, form.lines).filter((row) => row.change !== "Unchanged") : []),
    [catalog, original, form],
  );
  const reordered = original && form ? orderChanged(original.lines, form.lines) : false;
  const newVersion = used && (changes.length > 0 || reordered);
  const nextLabel = original ? versionLabel(original.version + 1) : "V1";
  const errors = useMemo(
    () => (form ? validateTemplateForm(catalog, form, { templates, editing, needsNote: newVersion }) : {}),
    [catalog, form, templates, editing, newVersion],
  );
  const grossValue = Number(gross) > 0 ? Number(gross) : grossFor(form?.pay_basis);

  // Server preview of the unsaved structure, half a second after the last
  // change; the browser's own calculation shows meanwhile and if it fails.
  const previewKey = form ? JSON.stringify([form.lines, grossValue]) : "";
  useEffect(() => {
    if (!open || !onPreview || !form?.lines.length) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      onPreview(form.lines, grossValue, controller.signal)
        .then((value) => { if (!controller.signal.aborted) setServerPreview(value ? { key: previewKey, value } : null); })
        .catch(() => { if (!controller.signal.aborted) setServerPreview(null); });
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onPreview, previewKey]);

  if (!form) return null;

  // An answer for earlier lines or another gross isn't used.
  const currentPreview = serverPreview?.key === previewKey ? serverPreview.value : null;
  const result = mergePreview(evaluateTemplate(catalog, form.lines, grossValue), currentPreview, grossValue);
  const beforeResult = newVersion ? evaluateTemplate(catalog, original.lines, grossValue) : null;
  const formulaErrors = lineErrors(catalog, form.lines);
  const formulasOk = !Object.keys(formulaErrors).length && !result.unresolved.length;
  const balanceError =
    form.lines.length && !result.balanced
      ? `Earnings add up to ${money(result.earnings)}, not GROSS (${money(grossValue)}). Adjust the values, or use Set as Balancing on one earning to take the difference.`
      : "";
  const set = (key) => (value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setServerErrors((current) => (current[key] ? { ...current, [key]: undefined } : current));
  };
  const setLines = (lines) => setForm((current) => ({ ...current, lines }));
  const error = (key) => serverErrors[key] || (showErrors ? errors[key] : undefined);
  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length || result.unresolved.length || balanceError) return;
    const outcome = await onSave(form, { newVersion, used, previewGross: grossValue });
    if (outcome?.fieldErrors) setServerErrors(outcome.fieldErrors);
  };
  const title = editing ? `Edit ${editing.name}` : "Add Template";
  const employees = editing ? templateEmployeeCount(editing) : 0;
  const sameBasis = editing && editing.pay_basis === form.pay_basis;
  const grossHint =
    sameBasis && editing.preview_gross > 0
      ? "Saved with the template"
      : sameBasis && editing.employees.length
        ? `Median of ${editing.employees.length} employees`
        : `Default for ${form.pay_basis}`;
  const changeBullets = [
    ...changes.map((row) => {
      if (row.change === "Upgraded") return <><b>Upgraded</b> {row.name} {versionLabel(row.fromVersion)} → {versionLabel(row.toVersion)} ({row.before} → {row.after})</>;
      if (row.change === "Added") return <><b>Added</b> {row.name}</>;
      if (row.change === "Removed") return <><b>Removed</b> {row.name}</>;
      return <><b>Changed</b> {row.name} ({row.before} → {row.after})</>;
    }),
    ...(reordered ? [<><b>Reordered</b> the payslip lines</>] : []),
  ];
  const basisOptions = payBases?.length ? PAY_BASIS_OPTIONS.filter((item) => payBases.includes(item.value)) : PAY_BASIS_OPTIONS;
  const reviewCount = (form.review_codes || []).length;

  let banner;
  if (reviewCount) {
    banner = (
      <RequestInfoBanner
        tone="warning"
        title={`Check ${plural(reviewCount, "fixed amount")} for ${form.pay_basis} pay.`}
        body={`The pay basis changed, so the highlighted fixed amounts may still be ${
          editing && editing.pay_basis !== form.pay_basis
            ? editing.pay_basis.toLowerCase()
            : form.pay_basis === "Monthly"
              ? "weekly or daily"
              : "monthly"
        } figures.`}
      />
    );
  } else if (!editing) {
    banner = <RequestInfoBanner title="The template is saved as V1." body="You can change it freely until it's assigned to an employee. After that, changes save as a new version." />;
  } else if (newVersion) {
    banner = (
      <RequestInfoBanner
        tone="warning"
        title={`This template is used by ${employees} employees. Saving creates ${nextLabel}.`}
        body="Employees stay on their current version until you revise them."
      />
    );
  } else if (used) {
    banner = (
      <RequestInfoBanner
        title={`Used by ${employees} employees.`}
        body={`Name and description save in place. Component changes save as ${nextLabel} with a change note; pay basis is locked.`}
      />
    );
  } else {
    banner = <RequestInfoBanner title="Not assigned to any employee." body="Every change saves in place." />;
  }

  const side = (
    <Mui.Stack spacing={2}>
      <PreviewCard
        catalog={catalog}
        form={form}
        original={original}
        gross={gross}
        setGross={setGross}
        grossHint={grossHint}
        result={result}
        beforeResult={beforeResult}
        newVersionLabel={nextLabel}
        beforeLabel={original ? versionLabel(original.version) : ""}
      />
      <CheckLine ok={result.balanced}>
        {result.balanced
          ? `Earnings add up to GROSS (${money(grossValue)})`
          : `Earnings add up to ${money(result.earnings)}, not GROSS (${money(grossValue)}). Adjust the values, or use Set as Balancing on one earning to take the difference.`}
      </CheckLine>
      <CheckLine ok={formulasOk}>
        {formulasOk
          ? "All formulas valid · no circular references"
          : result.unresolved.length && !Object.keys(formulaErrors).length
            ? `Circular or missing reference: ${result.unresolved.join(", ")}`
            : `Fix the value for ${Object.keys(formulaErrors).map((code) => lineName(catalog, form.lines.find((line) => line.code === code) || { code })).join(", ")}`}
      </CheckLine>
      {result.errors.map((message) => <CheckLine key={message} ok={false}>{message}</CheckLine>)}
      {newVersion ? (
        <FormSection title={`New Version · ${nextLabel}`} info="What changes. Employees keep their version until revised.">
          <Mui.Box component="ul" sx={{ gridColumn: "1 / -1", m: 0, pl: 2.5, "& li": { fontSize: 14, color: designColors.slate700, mb: 0.5 } }}>
            {changeBullets.map((bullet, index) => <li key={index}>{bullet}</li>)}
          </Mui.Box>
          <Field wide label={<>Change Note<Required /></>} helper="Why the template is changing. Shown in the Activity Log." error={error("note")}>
            <Mui.TextField size="small" value={form.note} onChange={(event) => set("note")(event.target.value)} multiline minRows={3} placeholder="Why is this template changing?" error={Boolean(error("note"))} disabled={busy} sx={multilineFieldSx} />
          </Field>
        </FormSection>
      ) : null}
    </Mui.Stack>
  );

  return (
    <GlobalSidePanel
      open={open}
      onClose={busy ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title={title}
      subtitle={editing ? (used ? `Used by ${employees} employees` : "Not assigned to any employee") : "Pick the components and their default calculation."}
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (!busy) history.push(PAYROLL_HOME_PATH); } },
        "Salary",
        { label: "Salary Templates", onClick: () => { if (!busy) onClose(); } },
        title,
      ]}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={onClose}
          primaryLabel={busy ? "Saving..." : editing ? (newVersion ? `Save as ${nextLabel}` : "Save Changes") : "Save Template"}
          primaryProps={{ disabled: busy, onClick: save }}
        />
      }
    >
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: expanded ? { xs: "1fr", lg: "minmax(0, 1fr) 380px" } : "1fr",
          gap: 2,
          alignItems: "start",
        }}
      >
        <Mui.Stack spacing={2} sx={{ minWidth: 0 }}>
          {banner}
          <FormSection title="Template Details" info="How the template is named and paid.">
            <Field wide label={<>Template Name<Required /></>} helper="Shown in Assign Salary and Salary Revision." error={error("name")}>
              <TextField value={form.name} onChange={set("name")} disabled={busy} error={error("name")} placeholder="e.g. Workers — Daily" />
            </Field>
            <Field wide label="Description" helper="Optional. Who the template is for.">
              <TextField value={form.description} onChange={set("description")} disabled={busy} placeholder="Who is this template for?" />
            </Field>
            {used ? (
              <Field wide label={<Mui.Stack direction="row" spacing={0.5} alignItems="center" component="span"><span>Pay Basis</span><LockOutlined sx={{ fontSize: 14 }} /></Mui.Stack>} helper="Locked once the template is used. Use Duplicate as… for a Daily or Weekly copy.">
                <Mui.TextField size="small" value={form.pay_basis} disabled sx={multilineFieldSx} />
              </Field>
            ) : (
              <Field wide label={<>Pay Basis<Required /></>} helper={`Only employees with a ${form.pay_basis} salary type can be given this template.`}>
                <MethodCards
                  value={form.pay_basis}
                  options={basisOptions}
                  disabled={busy}
                  onChange={(value) => {
                    set("pay_basis")(value);
                    setGross(String(grossFor(value)));
                    if (editing) {
                      setForm((current) => ({
                        ...current,
                        review_codes:
                          value === editing.pay_basis
                            ? []
                            : current.lines.filter((line) => line.method === "Fixed Amount" && !line.balancing).map((line) => line.code),
                      }));
                    }
                  }}
                />
              </Field>
            )}
          </FormSection>
          {showErrors && errors.lines ? <RequestInfoBanner tone="danger" body={errors.lines} /> : null}
          {visibleCategories(catalog, form.lines).map((category) => (
            <LinesEditor key={category} catalog={catalog} category={category} form={form} setLines={setLines} original={original} result={result} gross={grossValue} errors={formulaErrors} showErrors={showErrors} />
          ))}
          <RequestInfoBanner body="Pay Input components (bonus, overtime, penalties, loans) and Statutory PF, ESI and PT are added at payroll, so they can't be added to a template." />
        </Mui.Stack>
        <Mui.Box sx={expanded ? { position: { lg: "sticky" }, top: { lg: 0 } } : undefined}>{side}</Mui.Box>
      </Mui.Box>
    </GlobalSidePanel>
  );
};

// ---------------- Duplicate dialog ----------------

// "Duplicate as…": name + pay basis for the copy (template-duplicate).
export const DuplicateTemplateDialog = ({ open, template, payBases, busy, onCancel, onConfirm }) => {
  const [name, setName] = useState("");
  const [payBasis, setPayBasis] = useState("Monthly");
  useEffect(() => {
    if (open && template) {
      setName(`${template.name} (copy)`);
      setPayBasis(template.pay_basis);
    }
  }, [open, template]);
  if (!template) return null;
  const options = payBases?.length ? payBases : PAY_BASIS_OPTIONS.map((item) => item.value);
  return (
    <GlobalConfirmDialog
      open={open}
      title={`Duplicate ${template.name}`}
      onCancel={() => { if (!busy) onCancel(); }}
      onConfirm={() => onConfirm(name, payBasis)}
      confirmLabel="Duplicate"
      cancelLabel="Cancel"
      loading={busy}
      loadingLabel="Duplicating..."
      confirmDisabled={!name.trim()}
      maxWidth="sm"
    >
      <Mui.Stack spacing={2}>
        <Mui.Box>Creates a new Unused V1 with the same components. Changing the pay basis flags the fixed amounts to re-check.</Mui.Box>
        <Field label={<>Template Name<Required /></>} helper="Name of the new template.">
          <TextField value={name} onChange={setName} disabled={busy} placeholder="e.g. Staff — Weekly" />
        </Field>
        <Field label={<>Pay Basis<Required /></>} helper="Pay basis of the new template.">
          <SelectField value={payBasis} onChange={setPayBasis} options={toOptions(options)} disabled={busy} placeholder="Select pay basis" />
        </Field>
      </Mui.Stack>
    </GlobalConfirmDialog>
  );
};
